import React, { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { continueSessionStreaming, initializeChatSession, updateChatSessionHeaders } from "services/AgentChatService";
import { normalizeWorkflowTagsForSend } from "modules/chat/utils/chatUtils";
import { getAnalyst } from "services/AnalystService";
import { listScheduledJobsByOwnerId } from "services/ScheduleServices";
import { ArtifactKind, ArtifactMap } from "modules/artifacts/utils/artifactUtils";
import {
  AgentInputProto,
  ArtifactProto,
  ChatClientType,
  chatClientTypeToJSON,
  ChatContext,
  ChatMessageProto,
  ChatRequest,
  ChatResponse,
} from "build-ts/chat_session";
import {
  ChatMessageType,
  chatMessageTypeFromJSON,
  DarwinAgentType,
  DarwinHeader,
  ExecutionStatusType,
} from "build-ts/common";
import { Header } from "build-ts/metadata";
import { ScheduledJobProto, ScheduledJobType } from "build-ts/schedule";
import { extractScenarioJobsSavedByTools } from "utils/helperFuncUtils";
import { generateRandomSuffix } from "utils/nameUtils";
import showToast from "hooks/useCustomToast";
import { useChatContext } from "context/ChatContext";
import { useUIContext } from "context/UIContext";
import { ArtifactOpenOpts, ChatMessage } from "../../interfaces";
import { useChatStreaming } from "../hooks/useChatStreaming";
import ChatPanel from "../components/ChatPanel";

export interface ChatContainerProps {
  userId?: string;
  isLoadingChat: boolean;
  chatId: string | undefined;
  setChatId: React.Dispatch<React.SetStateAction<string | undefined>>;
  chatMessages: ChatMessage[];
  setChatMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  sessionId?: string;
  onArtifactClick?: (artifact: ArtifactProto, opts?: ArtifactOpenOpts) => void;
  selectedAnalyst?: Header | null;
  cleanContent?: (content: string) => string;
  analystsList?: Header[];
  isLoadingAnalysts?: boolean;
  isFromEva?: boolean;
  onAnalystChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  isArtifactsPanelVisible?: boolean;
  /** Registers one or more artifacts into the store on each streaming event. */
  registerArtifacts?: (...artifacts: ArtifactProto[]) => void;
  /** Resolves any artifact/sub-resource name to its openable ArtifactProto; forwarded to MarkdownRenderer. */
  lookupByName?: (name: string, kind?: ArtifactKind) => ArtifactProto | undefined;
  /** Full session artifact registry — forwarded to ChatPanel for the @ mention menu. */
  artifactMap?: ArtifactMap;
  isChatSaved?: boolean;
  initialPrompt?: string;
  /** When set, auto-send waits until selectedAnalyst.id matches before firing */
  initialAnalystId?: string;
  scenarioJobs?: ScheduledJobProto[];
  setScenarioJobs?: React.Dispatch<React.SetStateAction<ScheduledJobProto[]>>;
  setScenarioJobsSavedByTools?: React.Dispatch<React.SetStateAction<ScheduledJobProto[]>>;
}

const ChatContainer: React.FC<ChatContainerProps> = props => {
  const {
    userId = "",
    chatId,
    setChatId,
    setChatMessages,
    isFromEva = false,
    isChatSaved,
    cleanContent,
    registerArtifacts,
    setScenarioJobs,
    setScenarioJobsSavedByTools,
    initialPrompt,
    initialAnalystId,
    chatMessages,
    selectedAnalyst,
  } = props;

  const navigate = useNavigate();
  const { currentSessionHeader, setCurrentSessionHeader, setAnalystProto } = useChatContext();
  const { setIsNavbarCollapsed } = useUIContext();

  // Guards against sending the same initialPrompt twice.
  const lastSentInitialPromptRef = useRef<string | null>(null);

  const {
    isLoading,
    setIsLoading,
    streamingStatusLabel,
    setStreamingStatusLabel,
    prepareForNewTurn,
    finalizeStreamingTurn,
    appendStreamingToken,
    handleExecutionStep,
    handleReasoningToken,
    applyStreamChunk,
  } = useChatStreaming({
    setChatMessages,
    registerArtifacts,
    setScenarioJobsSavedByTools,
    onArtifactClick: props.onArtifactClick,
  });

  // ── Core send handler ─────────────────────────────────────────────────────

  const handleSendMessage = useCallback(
    async (inputMsg: string, newChat: boolean, analyst: Header | null | undefined) => {
      const msgToSend = normalizeWorkflowTagsForSend(inputMsg.trim());
      if (!msgToSend || (!chatId && !newChat)) return;

      const userMsg = { id: crypto.randomUUID(), content: inputMsg.trim(), author: "user" as const };
      const loadingMsg = { id: crypto.randomUUID(), content: "", author: "ai" as const };

      // Reset all streaming and reasoning state for the new turn.
      prepareForNewTurn();

      setChatMessages(prev => [...prev, userMsg, loadingMsg]);
      setIsLoading(true);

      try {
        let response: ChatResponse | undefined;
        let wasStreamed = false;

        if (newChat) {
          if (!analyst) {
            showToast("Please select an analyst first", "", "warning");
            return;
          }
          const analystDetails = await getAnalyst(analyst.id!);
          if (!analystDetails) {
            showToast("Failed to load analyst details", "", "error");
            return;
          }
          setAnalystProto(analystDetails);

          const jobsForNewChat: ScheduledJobProto[] = [];
          if (analystDetails?.header?.id) {
            const jobs = await listScheduledJobsByOwnerId(analystDetails.header.id);
            if (jobs && jobs.length > 0) {
              jobsForNewChat.push(
                ...jobs.filter((job: ScheduledJobProto) => job.type === ScheduledJobType.SCENARIO_JOB)
              );
            }
          }
          if (jobsForNewChat.length > 0) {
            setScenarioJobs?.(jobsForNewChat);
          }

          const envAgentType = process.env.REACT_APP_AGENT_TYPE_FOR_CHAT;
          const agentTypeFromEnv: DarwinAgentType =
            envAgentType !== undefined && envAgentType !== null && envAgentType !== ""
              ? (Number(envAgentType) as DarwinAgentType)
              : DarwinAgentType.ANALYST_CHAT_AGENT;

          const chatContext = ChatContext.create({
            agent_type: agentTypeFromEnv,
            agent_input: AgentInputProto.create({
              analyst: analystDetails,
              scenario_jobs: jobsForNewChat,
            }),
          });

          const initResponse = await initializeChatSession(
            ChatRequest.create({
              session_header: DarwinHeader.create({
                id: "",
                name: `Chat ${generateRandomSuffix(8)}`,
                type: chatClientTypeToJSON(ChatClientType.FRIDA),
                author_id: userId,
              }),
              chat_context: chatContext,
              is_save: true,
              stream_response: false,
            })
          );

          if (!initResponse?.session_header?.id) {
            showToast("Failed to create new chat session", "", "error");
            return;
          }

          const newSessionId = initResponse.session_header.id;
          setChatId(newSessionId);
          const sessionHeader = { ...initResponse.session_header, name: "" };
          setCurrentSessionHeader(sessionHeader);
          navigate(`/chat/${newSessionId}`, { replace: true });

          // Fire naming agent in the background
          let generatedTitle: string | undefined;
          initializeChatSession(
            ChatRequest.create({
              session_header: DarwinHeader.create({
                id: "",
                type: chatClientTypeToJSON(ChatClientType.FRIDA),
                author_id: userId,
              }),
              chat_context: ChatContext.create({
                agent_type: DarwinAgentType.CHAT_NAMING_AGENT,
              }),
              message: msgToSend,
              is_save: false,
            })
          )
            .then(resp => {
              const titleMsg = resp?.messages?.find(
                m => chatMessageTypeFromJSON(m.message_type) === ChatMessageType.RESPONSE && m.content
              );
              generatedTitle = titleMsg?.content?.trim();
              if (generatedTitle) {
                setCurrentSessionHeader(prev => (prev ? { ...prev, name: generatedTitle! } : prev));
                updateChatSessionHeaders([newSessionId], generatedTitle)
                  .then(() => {
                    window.dispatchEvent(
                      new CustomEvent("chat:title-updated", {
                        detail: { sessionId: newSessionId, title: generatedTitle },
                      })
                    );
                  })
                  .catch(err => console.warn("Failed to apply generated title:", err));
              }
            })
            .catch(err => console.warn("Session naming failed (non-critical):", err));

          wasStreamed = true;
          await continueSessionStreaming(
            newSessionId,
            msgToSend,
            isFromEva ? isChatSaved : true,
            userId,
            appendStreamingToken,
            chunk => {
              applyStreamChunk(chunk, value => {
                response = value;
              });
            },
            handleExecutionStep,
            handleReasoningToken
          );
          finalizeStreamingTurn();
        } else {
          // Continue existing chat
          wasStreamed = true;
          await continueSessionStreaming(
            chatId!,
            msgToSend,
            isFromEva ? isChatSaved : true,
            userId,
            appendStreamingToken,
            chunk => {
              applyStreamChunk(chunk, value => {
                response = value;
              });
            },
            handleExecutionStep,
            handleReasoningToken
          );
          finalizeStreamingTurn();
        }

        if (response?.status === ExecutionStatusType.TOOL_CALL_FAILURE) {
          setChatMessages(prev => [
            ...prev.slice(0, -1),
            {
              id: crypto.randomUUID(),
              author: "ai",
              content: "",
              executionStatus: ExecutionStatusType.TOOL_CALL_FAILURE,
            },
          ]);
          return;
        }

        if (!wasStreamed) {
          // Non-streaming path
          const newArtifacts: ArtifactProto[] = [];
          if (response && response.messages) {
            const lastHumanMsgIdx = response.messages.reduce((lastIdx, msg, idx) => {
              if (chatMessageTypeFromJSON(msg.message_type) === ChatMessageType.HUMAN && msg.content === msgToSend) {
                return idx;
              }
              return lastIdx;
            }, -1);

            const reasoningMessages = response.messages.filter(
              (msg, idx) =>
                idx > lastHumanMsgIdx &&
                chatMessageTypeFromJSON(msg.message_type) === ChatMessageType.REASONING &&
                (msg as ChatMessageProto).execution_steps.length > 0
            );
            const messagesToProcess =
              reasoningMessages.length > 0 ? [reasoningMessages[reasoningMessages.length - 1]] : reasoningMessages;

            for (const msg of messagesToProcess) {
              for (const artifact of (msg as ChatMessageProto).execution_steps.flatMap(s => s.artifacts)) {
                newArtifacts.push(artifact);
              }
            }
          }

          if (newArtifacts.length > 0 && registerArtifacts) {
            registerArtifacts(...newArtifacts);
          }

          setChatMessages(prev => {
            const updated = prev.slice(0, -1);
            if (!response || !response.messages) return updated;

            const userMsgIdx = response.messages.reduce((lastIdx, msg, idx) => {
              if (chatMessageTypeFromJSON(msg.message_type) === ChatMessageType.HUMAN && msg.content === msgToSend) {
                return idx;
              }
              return lastIdx;
            }, -1);
            const thisTurnMessages = userMsgIdx >= 0 ? response.messages.slice(userMsgIdx + 1) : response.messages;

            let responseMsg: ChatMessageProto | undefined;
            let reasoningMsg: ChatMessageProto | undefined;
            for (let i = thisTurnMessages.length - 1; i >= 0; i--) {
              const msg = thisTurnMessages[i] as ChatMessageProto;
              const msgType = chatMessageTypeFromJSON(msg.message_type);
              if (msgType === ChatMessageType.RESPONSE && !responseMsg) {
                responseMsg = msg;
              } else if (msgType === ChatMessageType.REASONING && !reasoningMsg) {
                reasoningMsg = msg;
                if (responseMsg) break;
              } else if (msgType === ChatMessageType.HUMAN) {
                break;
              }
            }

            // The human message is at response.messages[userMsgIdx] — NOT in thisTurnMessages
            // (which starts at userMsgIdx + 1).
            const humanMsg = userMsgIdx >= 0 ? (response.messages[userMsgIdx] as ChatMessageProto) : undefined;
            if (humanMsg?.timestamp_ms) {
              for (let k = updated.length - 1; k >= 0; k--) {
                if (updated[k].author === "user") {
                  updated[k] = { ...updated[k], userTimestampMs: Number(humanMsg.timestamp_ms) };
                  break;
                }
              }
            }

            if (!responseMsg) return updated;

            const steps = reasoningMsg?.execution_steps ?? [];
            const artifactsForMessage = steps.flatMap(s => s.artifacts);
            // prevMessageEndTimestampMs: end time of the message just before this
            // AI turn — used to compute the first reasoning block's duration.
            // Rule: if the human message has execution steps, use last step's
            // timestamp; otherwise use the human message's own timestamp.
            let prevMessageEndTimestampMs: number | undefined;
            if (humanMsg) {
              const humanSteps = (humanMsg as ChatMessageProto).execution_steps ?? [];
              const lastHumanStep = humanSteps[humanSteps.length - 1];
              prevMessageEndTimestampMs = lastHumanStep?.timestamp_ms
                ? Number(lastHumanStep.timestamp_ms)
                : humanMsg.timestamp_ms
                  ? Number(humanMsg.timestamp_ms)
                  : undefined;
            }

            if (responseMsg.content && responseMsg.content.length > 0) {
              updated.push({
                id: crypto.randomUUID(),
                author: "ai",
                content: cleanContent ? cleanContent(responseMsg.content) : responseMsg.content,
                ls_trace_url: responseMsg.ls_trace_url,
                executionSteps: steps.length > 0 ? steps : undefined,
                prevMessageEndTimestampMs,
                artifacts: artifactsForMessage.length > 0 ? artifactsForMessage : undefined,
                langgraph_message_json_list: reasoningMsg?.langgraph_message_json_list?.length
                  ? reasoningMsg.langgraph_message_json_list
                  : [],
              });
            } else if (responseMsg.response_message) {
              updated.push({
                id: crypto.randomUUID(),
                author: "ai",
                content: "",
                responseMessage: responseMsg.response_message,
                ls_trace_url: responseMsg.ls_trace_url,
                executionSteps: steps.length > 0 ? steps : undefined,
                prevMessageEndTimestampMs,
                artifacts: artifactsForMessage.length > 0 ? artifactsForMessage : undefined,
                langgraph_message_json_list: reasoningMsg?.langgraph_message_json_list?.length
                  ? reasoningMsg.langgraph_message_json_list
                  : [],
              });
            }
            return updated;
          });
        } else {
          // Streaming path: apply cleanContent on final text
          setChatMessages(prev => {
            const updated = [...prev];
            const lastIdx = updated.length - 1;
            if (updated[lastIdx]?.author === "ai" && cleanContent && updated[lastIdx].content) {
              updated[lastIdx] = {
                ...updated[lastIdx],
                content: cleanContent(updated[lastIdx].content),
              };
            }
            return updated;
          });
        }
      } catch (error) {
        setChatMessages(prev => prev.slice(0, -1));
        console.error("Error sending message:", error);
      } finally {
        setIsLoading(false);
        setStreamingStatusLabel("Thinking ...");
      }
    },
    [
      chatId,
      userId,
      isFromEva,
      isChatSaved,
      cleanContent,
      navigate,
      setChatId,
      setChatMessages,
      registerArtifacts,
      setScenarioJobs,
      setCurrentSessionHeader,
      setIsLoading,
      setStreamingStatusLabel,
      prepareForNewTurn,
      appendStreamingToken,
      applyStreamChunk,
      finalizeStreamingTurn,
      handleExecutionStep,
      handleReasoningToken,
    ]
  );

  // ── Wrap send with navbar collapse side-effect ────────────────────────────
  const onSendMessage = useCallback(
    async (inputMsg: string, newChat: boolean, analyst: Header | null | undefined) => {
      setIsNavbarCollapsed(true);
      await handleSendMessage(inputMsg, newChat, analyst);
    },
    [setIsNavbarCollapsed, handleSendMessage]
  );

  // ── Auto-send initialPrompt when set (e.g. from workflow Run/Edit/Create buttons) ──
  // When initialAnalystId is provided, wait until selectedAnalyst.id matches it so we
  // never fire with the default analyst while the correct one is still being resolved.
  useEffect(() => {
    if (
      !initialPrompt?.trim() ||
      chatId !== undefined ||
      isLoading ||
      !selectedAnalyst ||
      (initialAnalystId && selectedAnalyst.id !== initialAnalystId) ||
      lastSentInitialPromptRef.current === initialPrompt
    ) {
      return;
    }
    lastSentInitialPromptRef.current = initialPrompt;
    handleSendMessage(initialPrompt.trim(), true, selectedAnalyst);
  }, [initialPrompt, chatId, isLoading, selectedAnalyst, initialAnalystId]);

  // ── Derive saved scenario jobs from last message after streaming ends ─────
  useEffect(() => {
    if (isLoading || chatMessages.length === 0 || !setScenarioJobsSavedByTools) return;
    const lastMessage = chatMessages[chatMessages.length - 1];
    if (!lastMessage) return;
    const extracted = extractScenarioJobsSavedByTools([lastMessage]);
    setScenarioJobsSavedByTools(prev => {
      if (prev.length === extracted.length && prev.every((job, i) => job === extracted[i])) {
        return prev;
      }
      return extracted;
    });
  }, [chatMessages, isLoading, setScenarioJobsSavedByTools]);

  // True while an initialPrompt is waiting to be auto-sent (analysts/workflows still loading).
  // This will help us to show loading state in the chat panel while the initial prompt is being sent.
  // This is useful when the user clicks on the "Run Workflow" button and the workflow is being executed.
  const isAutoSendPending = !!initialPrompt?.trim() && lastSentInitialPromptRef.current !== initialPrompt;

  return (
    <ChatPanel
      isLoadingChat={props.isLoadingChat}
      chatId={props.chatId}
      chatMessages={props.chatMessages}
      sessionId={props.sessionId}
      onArtifactClick={props.onArtifactClick}
      selectedAnalyst={props.selectedAnalyst}
      analystsList={props.analystsList}
      isLoadingAnalysts={props.isLoadingAnalysts}
      isFromEva={props.isFromEva}
      onAnalystChange={props.onAnalystChange}
      isArtifactsPanelVisible={props.isArtifactsPanelVisible}
      lookupByName={props.lookupByName}
      artifactMap={props.artifactMap}
      scenarioJobs={props.scenarioJobs}
      isLoading={isLoading}
      streamingStatusLabel={streamingStatusLabel}
      onSendMessage={onSendMessage}
      isAutoSendPending={isAutoSendPending}
      currentSessionHeader={currentSessionHeader}
    />
  );
};

export default ChatContainer;
